import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Trans, useTranslation } from "react-i18next";

import clsx from "clsx";
import dayjs from "dayjs";

import get from "lodash/get";
import map from "lodash/map";
import times from "lodash/times";
import reduce from "lodash/reduce";

import Typography from "@mui/material/Typography";

import useSetState from "react-use/lib/useSetState";
import useClickAway from "react-use/lib/useClickAway";

import useIdleEffect from "../../lib/hooks/useIdleEffect";
import {
  useIdentificationCode,
  useOverview,
} from "../../lib/hooks/useQueryBoard";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import { client } from "../../bit/board/hooks/useGraphQLClient";

import Modal from "../../components/Modal";
import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import List from "../../components/List";
import Curve from "../../components/Animations/PageTransitions/Curve";
import MoveUp from "../../components/Animations/MoveUp";

import { PinInput } from "../Check";

import { QUERY_CURRENTLY_CHECKED_IN } from "../../config/graphql/query";
import { isVirtualKeyboardEnabled, isInWebView } from "../../config/const";

const defaultPinCode = {
  1: "",
  2: "",
  3: "",
  4: "",
  5: "",
  6: "",
};

const getOwnerField = (
  owner: IEmployee | IMeetingAttendee | IGuest | undefined,
  fieldName: string,
  identificationCode: string,
): string => {
  if (!owner) {
    return "";
  }

  if (fieldName in owner) {
    return get(owner, [fieldName]);
  }

  if ("fields" in owner) {
    const field = owner.fields.find(
      (field) => field.name === fieldName || field.name === identificationCode,
    );

    return field ? field.value : "";
  }

  return "";
};

const OverviewPage = () => {
  const { t } = useTranslation(["overview"]);

  const [pinCode, setPinCode] = useSetState<{ [key: number]: string }>(
    defaultPinCode,
  );

  const [focusedInput, setFocusedInput] = useState<undefined | string>("1");

  const [status, setStatus] = useState<undefined | "success" | "error">();

  const [queryEnabled, setQueryEnabled] = useState<boolean>(false);

  const identificationCode = useIdentificationCode();

  const [overviewList, setOverviewList] = useState<
    {
      id: string;
      fullName: string;
      type: AccessEventType;
      checkinAt: string;
      host: string;
    }[]
  >();

  useQuery<{
    currentlyCheckedIn: { events: IAccessEvent[] };
  }>(
    ["currentlyCheckedIn"],
    ({ queryKey: [_key] }: any) =>
      client.request<{
        currentlyCheckedIn: { events: IAccessEvent[] };
      }>(QUERY_CURRENTLY_CHECKED_IN, {
        pagination: { limit: 100000 },
      }),
    {
      enabled: queryEnabled,
      onSuccess: (data) => {
        const list = map(data?.currentlyCheckedIn?.events, (accessEvent) => {
          const checkinAt = get(accessEvent, "metadata.checkIn.date");
          return {
            id: accessEvent.owner.id,
            fullName: getOwnerField(
              accessEvent.owner,
              "fullName",
              identificationCode,
            ),
            type: get(accessEvent, "type"),
            checkinAt: dayjs(checkinAt).format("DD-MM-YYYY / HH:mm"),
            host: get(accessEvent, "receiver.fullName") || "",
          };
        });
        setOverviewList(list);
      },
    },
  );

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const { code: overviewCode } = useOverview();

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const onKeyboardChange = useCallback(
    (value: string) => {
      const regex = /^\d$/;

      if (
        focusedInput &&
        +focusedInput < 7 &&
        (value === "" || regex.test(value))
      ) {
        /**
         * Reset virtual input after we set the code
         */
        if (keyboard.current) {
          keyboard.current.setInput("", focusedInput);
        }

        setPinCode({ [focusedInput]: value });

        if (focusedInput === "6" && value.length) {
          setFocusedInput(undefined);
        } else {
          if (document.activeElement) {
            (document.activeElement as HTMLInputElement)?.blur();
          }

          setFocusedInput(`${+focusedInput + 1}`);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button: string) => {
      if (button === "{bksp}" && typeof focusedInput !== "undefined") {
        if (pinCode[+focusedInput]) {
          setPinCode({ [focusedInput]: "" });
        } else {
          setPinCode({ [+focusedInput - 1]: "" });
          const nextFocusedInput = Math.max(1, +focusedInput - 1);
          setFocusedInput(`${nextFocusedInput}`);
        }
      }
    },
    [focusedInput],
  );

  const onPasteCapture = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const content = event.clipboardData.getData("Text");

      const isValidPin = /^[0-9]{1,6}$/g.test(content);

      if (!isValidPin) {
        return;
      }

      event.preventDefault();

      const pinCode = reduce(
        times(6),
        (acc, key) => ({
          ...acc,
          [key + 1]: content[key],
        }),
        {},
      );

      setPinCode(pinCode);
    },
    [],
  );

  const onFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement, Element>) => {
      const {
        target: { name },
      } = event;

      setFocusedInput(`${+name}`);

      if (keyboard.current) {
        keyboard.current.setInput("", name);
      }
    },
    [],
  );

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;

    /**
     * Blur when setting last input is set form keyboard
     */
    if (name === "6" && value && !!value.length) {
      event.target.blur();
    }

    const regex = /^\d$/;

    if (value === "" || regex.test(value)) {
      setPinCode({ [name]: value });

      let nextFocusedInput: number | undefined = +name;

      if (name === "6") {
        nextFocusedInput = undefined;
      } else if (value.length) {
        nextFocusedInput += 1;
      } else {
        nextFocusedInput -= 1;
      }

      setFocusedInput(nextFocusedInput ? `${nextFocusedInput}` : undefined);
    }
  }, []);

  const onBeforeInput = useCallback((event: InputEvent) => {
    switch (event.inputType) {
      case "deleteByComposition":
      case "deleteByCut":
      case "deleteByDrag":
      case "deleteEntireSoftLine":
      case "deleteSoftLineBackward":
      case "deleteSoftLineForward":
      case "deleteHardLineBackward":
      case "deleteHardLineForward":
      case "deleteWordBackward":
      case "deleteWordForward":
      case "deleteContent":
      case "deleteContentForward":
      case "deleteContentBackward": {
        event.preventDefault();

        const { name } = event?.currentTarget as HTMLInputElement;

        setPinCode({ [name]: "" });

        const nextFocusedInput = Math.max(1, +name - 1);

        setFocusedInput(`${nextFocusedInput}`);

        break;
      }

      default: {
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (!isInWebView) {
      return;
    }

    const ids = Object.keys(defaultPinCode);

    ids.forEach((id) => {
      const element = document.querySelector<HTMLInputElement>(`[id='${id}']`);

      element?.addEventListener("beforeinput", onBeforeInput);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      ids.forEach((id) => {
        const element = document.querySelector<HTMLInputElement>(
          `[id='${id}']`,
        );

        element?.removeEventListener("beforeinput", onBeforeInput);
      });
    };
  }, []);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const {
        code,
        currentTarget: { name },
      } = event;

      switch (code) {
        case "Backspace": {
          event.preventDefault();

          if (pinCode[+name]) {
            setPinCode({ [name]: "" });
          } else {
            setPinCode({ [+name - 1]: "" });
            const nextFocusedInput = Math.max(1, +name - 1);
            setFocusedInput(`${nextFocusedInput}`);
          }

          break;
        }

        case "ArrowRight": {
          event.preventDefault();

          const nextFocusedInput = Math.min(+name + 1, 6);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowLeft": {
          event.preventDefault();
          const nextFocusedInput = Math.max(1, +name - 1);

          setFocusedInput(`${nextFocusedInput}`);

          break;
        }

        case "ArrowUp":
        case "ArrowDown": {
          event.preventDefault();

          break;
        }

        default: {
          break;
        }
      }
    },
    [pinCode],
  );

  useClickAway(keyboardBox, (event) => {
    if (event.target && (event.target as HTMLElement)?.tagName === "INPUT") {
      return;
    }

    setFocusedInput(undefined);
  });

  /**
   * Auto-focus input
   */
  useEffect(() => {
    if (focusedInput) {
      const input = document.querySelector<HTMLInputElement>(
        `input[name="${focusedInput}"]`,
      );

      if (input && typeof input.focus === "function") {
        input.focus();
        input.select();
      }
    }
  }, [focusedInput]);

  useEffect(() => {
    const pinCodeArr = Object.values(pinCode);

    const timeout = setTimeout(() => {
      if (pinCodeArr.every((input) => input !== "")) {
        if (pinCodeArr.join("") === overviewCode) {
          setStatus("success");
          setQueryEnabled(true);
          return;
        }

        setStatus("error");
      }
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [pinCode]);

  const onDismiss = useCallback(async () => {
    if (status === "success") {
      setStatus(undefined);
    }

    if (status === "error") {
      setStatus(undefined);
      setPinCode(defaultPinCode);
      setFocusedInput("1");

      if (document.activeElement) {
        (document.activeElement as HTMLInputElement)?.blur();
      }
    }
  }, [status]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 4000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        {!overviewList ? (
          <>
            <Title className="page-title">
              <Typography variant="h1">{t(`overview:title`)}</Typography>
            </Title>
            <form className="pin__wrapper" autoComplete="off">
              {([1, 2, 3, 4, 5, 6] as const).map((i) => (
                <MoveUp key={i} delay={0.15 + 0.05 * i}>
                  <PinInput
                    id={`${i}`}
                    name={`${i}`}
                    min={0}
                    max={9}
                    type="password"
                    className="pin__input"
                    autoComplete="off"
                    onFocus={onFocus}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onPasteCapture={onPasteCapture}
                    value={pinCode[i]}
                    maxLength={1}
                    pattern="[0-9]*"
                  />
                </MoveUp>
              ))}
            </form>
          </>
        ) : (
          <>
            <Title className="page-title mb-5">
              <Typography variant="h3">{t(`overview:listTitle`)}</Typography>
            </Title>
            {overviewList.length !== 0 ? (
              <List rows={overviewList} />
            ) : (
              <Typography variant="h3" className="text-secondary">
                {t(`overview:noUsers`)}
              </Typography>
            )}
          </>
        )}

        <div
          ref={keyboardBox}
          className={clsx("keyboard ", { keyboard__out: !focusedInput })}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <VirtualKeyboard
            ref={keyboard}
            disableButtonHold
            layoutName="pin"
            inputName={focusedInput}
            onChange={onKeyboardChange}
            onKeyPress={onKeyboardKeyPress}
          />
        </div>
        <AnimatePresence mode="wait">
          {!!status && (
            <Modal
              title={<Trans i18nKey={`overview:status.${status}.title`} />}
              message={<Trans i18nKey={`overview:status.${status}.message`} />}
              status={status}
              onDismiss={onDismiss}
            />
          )}
        </AnimatePresence>
      </div>
    </Curve>
  );
};

export default OverviewPage;
